import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { profileService } from '../../../services/userService';
import type { ProfileAction } from '../../../services/authService';
import ActionChecklist from '../components/ActionChecklist';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';

interface ProfileModule {
    id: number;
    name: string;
    code: string;
    is_active: boolean;
    description?: string;
    actions: ProfileAction[];
}

// All modules available in the system
const AVAILABLE_MODULES: Array<{ code: string; name: string; description: string; icon: string }> = [
    { code: 'patients',      name: 'Pacientes',      description: 'Gestión de pacientes y su historia clínica', icon: 'fa-user-injured'         },
    { code: 'appointments',  name: 'Citas',          description: 'Agenda y gestión de citas médicas',          icon: 'fa-calendar-check'        },
    { code: 'consultations', name: 'Consultas',      description: 'Consultas ambulatorias y recetas',           icon: 'fa-stethoscope'           },
    { code: 'billing',       name: 'Facturación',    description: 'Facturas y cobros de consulta',              icon: 'fa-file-invoice-dollar'   },
    { code: 'pharmacy',      name: 'Farmacia',       description: 'Gestión de medicamentos',                    icon: 'fa-pills'                 },
    { code: 'lab',           name: 'Laboratorio',    description: 'Órdenes y resultados de laboratorio',        icon: 'fa-flask'                 },
    { code: 'imaging',       name: 'Imágenes',       description: 'Órdenes y visor de imágenes médicas',        icon: 'fa-x-ray'                 },
    { code: 'reports',       name: 'Reportes',       description: 'Estadísticas e informes del sistema',        icon: 'fa-chart-bar'             },
    { code: 'insurance',     name: 'Seguros',        description: 'Empresas y planes de seguros (ARS)',         icon: 'fa-shield-halved'         },
    { code: 'services',      name: 'Servicios',      description: 'Catálogo de servicios y precios',            icon: 'fa-list-check'            },
    { code: 'admin',         name: 'Administración', description: 'Usuarios, perfiles y configuración',         icon: 'fa-users-gear'            },
];

const ProfileFormPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [existingModules, setExistingModules] = useState<ProfileModule[]>([]);
    const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
    const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
    const [actionChanges, setActionChanges] = useState<Map<number, string[]>>(new Map());

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isEdit) return;
        setLoading(true);
        profileService.getById(Number(id))
            .then(profile => {
                setName(profile.name);
                setDescription(profile.description ?? '');
                const mods: ProfileModule[] = (profile as any).modules ?? [];
                setExistingModules(mods);
                setSelectedCodes(new Set(
                    mods.filter((m) => m.is_active).map((m) => m.code)
                ));
            })
            .catch(() => setError('Error al cargar perfil'))
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    const toggleModule = (code: string) => {
        setSelectedCodes(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    const toggleExpanded = (code: string) => {
        setExpandedModules(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    const handleActionsChange = (moduleId: number, activeCodes: string[]) => {
        setActionChanges(prev => {
            const next = new Map(prev);
            next.set(moduleId, activeCodes);
            return next;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) { setError('El nombre del perfil es obligatorio'); return; }
        setSaving(true);
        setError(null);

        try {
            let profileId = Number(id);

            if (!isEdit) {
                const created = await profileService.create({ name: name.trim(), description: description || undefined });
                profileId = created.id;
            } else {
                await profileService.update(profileId, { name: name.trim(), description: description || undefined });
            }

            // Sync modules: add new, update existing, delete removed
            const existingByCode = new Map(existingModules.map(m => [m.code, m]));

            for (const mod of AVAILABLE_MODULES) {
                const existing = existingByCode.get(mod.code);
                const shouldBeActive = selectedCodes.has(mod.code);

                if (!existing && shouldBeActive) {
                    // Create new module
                    await profileService.addModule(profileId, {
                        name: mod.name,
                        description: mod.description,
                        code: mod.code,
                        is_active: true,
                    });
                } else if (existing && existing.is_active !== shouldBeActive) {
                    // Toggle existing module
                    await profileService.updateModule(existing.id, { is_active: shouldBeActive });
                }
            }

            // Save action changes per module
            for (const [moduleId, activeCodes] of actionChanges.entries()) {
                await profileService.setModuleActions(profileId, moduleId, activeCodes);
            }

            navigate('/admin/profiles');
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.response?.data?.errors;
            if (typeof msg === 'object') {
                setError(Object.values(msg).flat().join(' '));
            } else {
                setError(msg || 'Error al guardar perfil');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Spinner variant="full-page" text="Cargando..." size="lg" />;
    }

    return (
        <>
            <div className="page-header">
                <div className="row">
                    <div className="col-sm-12">
                        <Breadcrumb items={[{ label: 'Perfiles', to: '/admin/profiles' }, { label: isEdit ? 'Editar Perfil' : 'Nuevo Perfil' }]} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-8">
                    <div className="card">
                        <div className="card-body">
                            <h4 className="card-title mb-1">{isEdit ? 'Editar Perfil' : 'Nuevo Perfil'}</h4>
                            <p className="text-muted mb-3">
                                Define qué módulos del sistema pueden acceder los usuarios con este perfil.
                            </p>

                            {error && (
                                <div className="alert alert-danger alert-dismissible">
                                    <button className="btn-close" onClick={() => setError(null)}></button>
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label">Nombre del Perfil *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                            placeholder="Ej: Recepcionista, Médico, Administrador"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Descripción</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={description}
                                            onChange={e => setDescription(e.target.value)}
                                            placeholder="Descripción opcional"
                                        />
                                    </div>
                                </div>

                                <h6 className="mb-1">Módulos permitidos</h6>
                                <p className="text-muted small mb-3">
                                    Seleccione los módulos a los que puede acceder este perfil.
                                    <strong> Un perfil sin módulos seleccionados tiene acceso a todo el sistema.</strong>
                                </p>

                                <div className="row g-3 mb-4">
                                    {AVAILABLE_MODULES.map(mod => {
                                        const active = selectedCodes.has(mod.code);
                                        return (
                                            <div key={mod.code} className="col-md-6 col-lg-4">
                                                <div
                                                    className={`p-3 rounded border cursor-pointer ${active ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary border-opacity-25'}`}
                                                    style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                                    onClick={() => toggleModule(mod.code)}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className={`rounded-circle d-flex align-items-center justify-content-center ${active ? 'bg-primary' : 'bg-light'}`}
                                                            style={{ width: 36, height: 36, flexShrink: 0 }}
                                                        >
                                                            <i className={`fas ${mod.icon} ${active ? 'text-white' : 'text-secondary'}`} style={{ fontSize: '14px' }}></i>
                                                        </div>
                                                        <div className="flex-grow-1 overflow-hidden">
                                                            <div className={`fw-semibold small ${active ? 'text-primary' : 'text-dark'}`}>{mod.name}</div>
                                                            <div className="text-muted" style={{ fontSize: '11px', lineHeight: 1.3 }}>{mod.description}</div>
                                                        </div>
                                                        <div>
                                                            <i className={`fas fa-${active ? 'check-square' : 'square'} ${active ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '18px' }}></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Action checklist per module (edit mode only) */}
                                {isEdit && existingModules.filter(m => selectedCodes.has(m.code)).length > 0 && (
                                    <div className="mb-4">
                                        <h6 className="mb-2">Acciones por módulo</h6>
                                        <p className="text-muted small mb-3">
                                            Expande un módulo para configurar las acciones permitidas.
                                            Un módulo sin acciones permite todo el acceso (compatibilidad).
                                        </p>
                                        <div className="accordion" id="moduleActions">
                                            {existingModules
                                                .filter(m => selectedCodes.has(m.code))
                                                .map(mod => {
                                                    const isExpanded = expandedModules.has(mod.code);
                                                    const currentActions = actionChanges.has(mod.id)
                                                        ? mod.actions.map(a => ({
                                                            ...a,
                                                            is_active: actionChanges.get(mod.id)!.includes(a.action_code),
                                                        }))
                                                        : mod.actions;
                                                    return (
                                                        <div key={mod.id} className="accordion-item">
                                                            <h2 className="accordion-header">
                                                                <button
                                                                    type="button"
                                                                    className={`accordion-button ${isExpanded ? '' : 'collapsed'}`}
                                                                    onClick={() => toggleExpanded(mod.code)}
                                                                >
                                                                    <span className="me-2">{mod.name}</span>
                                                                    <span className="badge bg-secondary">
                                                                        {currentActions.filter(a => a.is_active).length}/{currentActions.length}
                                                                    </span>
                                                                </button>
                                                            </h2>
                                                            {isExpanded && (
                                                                <div className="accordion-body">
                                                                    <ActionChecklist
                                                                        moduleCode={mod.code}
                                                                        actions={currentActions}
                                                                        onChange={(codes) => handleActionsChange(mod.id, codes)}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}

                                <div className="alert alert-info py-2">
                                    <i className="fa fa-info-circle me-1"></i>
                                    <strong>{selectedCodes.size}</strong> módulo(s) seleccionado(s)
                                    {selectedCodes.size === 0 && <span> — este perfil tendrá acceso a todo el sistema</span>}
                                </div>

                                <div className="d-flex gap-2 mt-3">
                                    <button type="submit" className="btn btn-primary" disabled={saving}>
                                        {saving
                                            ? <Spinner size="sm" text="Guardando..." />
                                            : <><i className="fa fa-save me-1"></i> {isEdit ? 'Actualizar Perfil' : 'Crear Perfil'}</>}
                                    </button>
                                    <Link to="/admin/profiles" className="btn btn-outline-secondary">
                                        Cancelar
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="card">
                        <div className="card-body">
                            <h6 className="card-title">Módulos seleccionados</h6>
                            {selectedCodes.size === 0 ? (
                                <div className="alert alert-warning py-2 mb-0">
                                    <small>Sin módulos = acceso total al sistema</small>
                                </div>
                            ) : (
                                <ul className="list-unstyled mb-0">
                                    {AVAILABLE_MODULES.filter(m => selectedCodes.has(m.code)).map(m => (
                                        <li key={m.code} className="d-flex align-items-center gap-2 mb-2">
                                            <i className={`fas ${m.icon} text-primary`} style={{ width: 20 }}></i>
                                            <span className="small">{m.name}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm p-0 ms-auto text-danger"
                                                onClick={() => toggleModule(m.code)}
                                                style={{ lineHeight: 1 }}
                                                aria-label="Quitar módulo"
                                            >
                                                <i className="fa fa-times"></i>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProfileFormPage;
